/**
 * Pure H OS · Workstream 03 · Raw Materials Library
 * ==================================================
 * Single source of truth for every ingredient. R&D formulas ONLY
 * reference rows from this library.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Beaker, Plus, Save, Trash2, Search, X, ShieldCheck, ExternalLink, Package,
} from "lucide-react";
import { Card, SectionHeader, Button, EmptyState } from "../../components/lib";
import { rawMaterials, RawMaterial, RMEnumMap } from "../../services/api";

function Pill({ tone = "neutral", children }: { tone?: "neutral" | "success" | "warning" | "danger" | "info"; children: React.ReactNode }) {
  const palette = {
    neutral: { bg: "var(--ph-neutral-tint)", fg: "var(--ph-ink-muted)" },
    success: { bg: "var(--ph-success-tint)", fg: "var(--ph-success)" },
    warning: { bg: "var(--ph-warning-tint)", fg: "var(--ph-warning)" },
    danger:  { bg: "var(--ph-danger-tint)",  fg: "var(--ph-danger)" },
    info:    { bg: "rgba(79,93,184,.10)",    fg: "var(--ph-ai)" },
  }[tone];
  return (
    <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-semibold rounded"
          style={{ background: palette.bg, color: palette.fg, letterSpacing: "0.10em", textTransform: "uppercase" }}>
      {children}
    </span>
  );
}

function ifraTone(status: string): "success" | "warning" | "danger" | "neutral" {
  return ({ compliant: "success", restricted: "warning", prohibited: "danger" } as any)[status] || "neutral";
}

export default function RawMaterialsPage() {
  const [rows, setRows] = useState<RawMaterial[]>([]);
  const [enums, setEnums] = useState<RMEnumMap | null>(null);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<RawMaterial | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [usagePopover, setUsagePopover] = useState<RawMaterial | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await rawMaterials.list("orvan", { q: q || undefined, category: category || undefined });
      setRows(list);
    } finally { setLoading(false); }
  }, [q, category]);

  useEffect(() => {
    rawMaterials.enums().then(setEnums).catch(() => setEnums(null));
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!flash) return;
    const t = setTimeout(() => setFlash(null), 3200);
    return () => clearTimeout(t);
  }, [flash]);

  const summary = useMemo(() => ({
    total:      rows.length,
    compliant:  rows.filter((r) => r.ifraStatus === "compliant").length,
    restricted: rows.filter((r) => r.ifraStatus === "restricted").length,
    prohibited: rows.filter((r) => r.ifraStatus === "prohibited").length,
  }), [rows]);

  const onArchive = async (rm: RawMaterial) => {
    
    if (!window.confirm(`Archive ${rm.code} · ${rm.name}? This can't be undone from the UI.`)) return;
    try {
      await rawMaterials.archive("orvan", rm.id);
      await load();
      setFlash(`Archived ${rm.code}`);
    } catch (e: any) {
      setFlash(e?.message || "Archive failed");
    }
  };

  return (
    <div className="p-6 space-y-4" data-testid="raw-materials-page">
      <SectionHeader
        eyebrow="Workstream 03 · R&D Support"
        title="Raw Materials Library"
        subtitle="Every ingredient exists here once · R&D formulas reference these materials"
      />

      {/* Summary */}
      <div className="grid grid-cols-4 gap-3">
        <Card padded testId="rm-tile-total">
          <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold">Total materials</div>
          <div className="text-2xl font-bold mt-1">{summary.total}</div>
        </Card>
        <Card padded testId="rm-tile-compliant">
          <div className="text-[10px] uppercase tracking-[0.16em] font-semibold" style={{ color: "var(--ph-success)" }}>IFRA compliant</div>
          <div className="text-2xl font-bold mt-1">{summary.compliant}</div>
        </Card>
        <Card padded testId="rm-tile-restricted">
          <div className="text-[10px] uppercase tracking-[0.16em] font-semibold" style={{ color: "var(--ph-warning)" }}>IFRA restricted</div>
          <div className="text-2xl font-bold mt-1">{summary.restricted}</div>
        </Card>
        <Card padded testId="rm-tile-prohibited">
          <div className="text-[10px] uppercase tracking-[0.16em] font-semibold" style={{ color: "var(--ph-danger)" }}>IFRA prohibited</div>
          <div className="text-2xl font-bold mt-1">{summary.prohibited}</div>
        </Card>
      </div>

      {/* Filters + New */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-sm">
          <Search size={14} className="absolute left-2 top-2.5 text-slate-400" />
          <input
            className="w-full pl-8 pr-3 py-2 text-sm border rounded"
            style={{ borderColor: "var(--ph-border-strong)" }}
            placeholder="Search by code, name, CAS, supplier…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="rm-search"
          />
        </div>
        <select
          className="px-3 py-2 text-sm border rounded"
          style={{ borderColor: "var(--ph-border-strong)" }}
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          data-testid="rm-filter-category"
        >
          <option value="">All categories</option>
          {enums?.category?.values.map((c) => (
            <option key={c} value={c}>{enums.category.labels[c] || c}</option>
          ))}
        </select>
        <div className="ml-auto">
          <Button variant="primary" onClick={() => setShowCreate(true)} testId="rm-new-btn">
            <Plus size={14} /> New material
          </Button>
        </div>
      </div>

      {/* Table */}
      <Card padded={false} testId="rm-table">
        {loading && <div className="p-8 text-sm text-slate-500 text-center">Loading raw materials…</div>}
        {!loading && !rows.length && (
          <EmptyState icon={Beaker} title="No materials yet" message="Add your first raw material to start building formulas." />
        )}
        {!loading && rows.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead style={{ background: "var(--ph-surface-alt)" }}>
                <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-slate-500">
                  <th className="px-4 py-2 font-semibold">Code</th>
                  <th className="px-4 py-2 font-semibold">Name</th>
                  <th className="px-4 py-2 font-semibold">Category</th>
                  <th className="px-4 py-2 font-semibold">CAS #</th>
                  <th className="px-4 py-2 font-semibold">IFRA</th>
                  <th className="px-4 py-2 font-semibold text-right">Cost</th>
                  <th className="px-4 py-2 font-semibold">Used in</th>
                  <th className="px-4 py-2 font-semibold">Supplier</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-t hover:bg-slate-50" style={{ borderColor: "var(--ph-border-subtle)" }}
                      data-testid={`rm-row-${r.code}`}>
                    <td className="px-4 py-2 font-mono text-xs">{r.code}</td>
                    <td className="px-4 py-2 font-medium">{r.name}</td>
                    <td className="px-4 py-2">
                      <span className="text-[11px] text-slate-600">{enums?.category?.labels[r.category] || r.category}</span>
                    </td>
                    <td className="px-4 py-2 text-xs text-slate-600 font-mono">{r.casNumber || "—"}</td>
                    <td className="px-4 py-2"><Pill tone={ifraTone(r.ifraStatus)}>{r.ifraStatus}</Pill></td>
                    <td className="px-4 py-2 text-right text-sm">
                      {r.costPerUnit !== null && r.costPerUnit !== undefined
                        ? `${r.currency} ${r.costPerUnit.toFixed(2)}/${r.unit}`
                        : "—"}
                    </td>
                    <td className="px-4 py-2">
                      <UsageCell rm={r} onOpen={() => setUsagePopover(r)} />
                    </td>
                    <td className="px-4 py-2 text-xs text-slate-600">{r.supplier || "—"}</td>
                    <td className="px-4 py-2 text-right">
                      <button onClick={() => setEditing(r)}
                              className="text-xs font-semibold underline decoration-dotted text-slate-600 hover:text-slate-900 mr-2"
                              data-testid={`rm-edit-${r.code}`}>
                        Edit
                      </button>
                      <button onClick={() => onArchive(r)}
                              className="text-slate-400 hover:text-red-600"
                              data-testid={`rm-archive-${r.code}`}>
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {(showCreate || editing) && (
        <MaterialModal
          material={editing}
          enums={enums}
          onClose={() => { setShowCreate(false); setEditing(null); }}
          onSaved={async (msg) => { setShowCreate(false); setEditing(null); await load(); setFlash(msg); }}
        />
      )}

      {usagePopover && (
        <UsagePopover rm={usagePopover} onClose={() => setUsagePopover(null)} />
      )}

      {flash && (
        <div className="fixed bottom-6 right-6 px-4 py-2 rounded shadow-lg z-50"
             style={{ background: "var(--ph-brand-primary, #1a1a1a)", color: "#F7F3EB", fontSize: 12 }}
             data-testid="rm-flash">
          {flash}
        </div>
      )}
    </div>
  );
}

function MaterialModal({ material, enums, onClose, onSaved }: {
  material: RawMaterial | null;
  enums: RMEnumMap | null;
  onClose: () => void;
  onSaved: (msg: string) => Promise<void>;
}) {
  const [code, setCode] = useState(material?.code || "");
  const [name, setName] = useState(material?.name || "");
  const [supplier, setSupplier] = useState(material?.supplier || "");
  const [casNumber, setCasNumber] = useState(material?.casNumber || "");
  const [category, setCategory] = useState(material?.category || "other");
  const [ifraStatus, setIfraStatus] = useState(material?.ifraStatus || "unknown");
  const [unit, setUnit] = useState(material?.unit || "g");
  const [costPerUnit, setCostPerUnit] = useState<string>(String(material?.costPerUnit ?? ""));
  const [stockOnHand, setStockOnHand] = useState<string>(String(material?.stockOnHand ?? ""));
  const [leadTimeDays, setLeadTimeDays] = useState<string>(String(material?.leadTimeDays ?? ""));
  const [safetyNotes, setSafetyNotes] = useState(material?.safetyNotes || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const onSubmit = async () => {
    setBusy(true); setErr(null);
    try {
      if (material) {
        await rawMaterials.patch("orvan", material.id, {
          name, supplier, casNumber, category, ifraStatus, unit,
          costPerUnit: costPerUnit === "" ? null : Number(costPerUnit),
          stockOnHand: stockOnHand === "" ? null : Number(stockOnHand),
          leadTimeDays: leadTimeDays === "" ? null : Number(leadTimeDays),
          safetyNotes,
        });
        await onSaved(`Updated ${material.code}`);
      } else {
        await rawMaterials.create("orvan", {
          code, name, supplier, category, unit,
          costPerUnit: costPerUnit === "" ? undefined : Number(costPerUnit),
          ifraStatus,
        });
        await onSaved(`Created ${code}`);
      }
    } catch (e: any) {
      setErr(e?.message || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
         style={{ background: "rgba(0,0,0,0.4)" }} onClick={onClose}>
      <div className="w-full max-w-2xl bg-white rounded shadow-2xl"
           onClick={(e) => e.stopPropagation()}
           data-testid="rm-modal">
        <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: "var(--ph-border-subtle)" }}>
          <div>
            <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold">Raw Material</div>
            <div className="text-lg font-bold">{material ? `Edit · ${material.code}` : "New material"}</div>
          </div>
          <button onClick={onClose} className="p-1 hover:opacity-70"><X size={18} /></button>
        </div>
        <div className="p-4 space-y-3">
          {err && (
            <div className="px-3 py-2 rounded text-xs" style={{ background: "var(--ph-danger-tint)", color: "var(--ph-danger)" }}>
              {err}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Code (immutable)">
              <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())}
                     disabled={!!material}
                     data-testid="rm-modal-code"
                     className="w-full px-3 py-2 text-sm border rounded font-mono"
                     style={{ borderColor: "var(--ph-border-strong)" }} />
            </FormField>
            <FormField label="Display name">
              <input value={name} onChange={(e) => setName(e.target.value)}
                     data-testid="rm-modal-name"
                     className="w-full px-3 py-2 text-sm border rounded"
                     style={{ borderColor: "var(--ph-border-strong)" }} />
            </FormField>
            <FormField label="Supplier">
              <input value={supplier} onChange={(e) => setSupplier(e.target.value)}
                     className="w-full px-3 py-2 text-sm border rounded"
                     style={{ borderColor: "var(--ph-border-strong)" }} />
            </FormField>
            <FormField label="CAS Number">
              <input value={casNumber} onChange={(e) => setCasNumber(e.target.value)}
                     className="w-full px-3 py-2 text-sm border rounded font-mono"
                     style={{ borderColor: "var(--ph-border-strong)" }} />
            </FormField>
            <FormField label="Category">
              <select value={category} onChange={(e) => setCategory(e.target.value)}
                      data-testid="rm-modal-category"
                      className="w-full px-3 py-2 text-sm border rounded"
                      style={{ borderColor: "var(--ph-border-strong)" }}>
                {enums?.category.values.map((c) => (
                  <option key={c} value={c}>{enums.category.labels[c] || c}</option>
                ))}
              </select>
            </FormField>
            <FormField label="IFRA Status">
              <select value={ifraStatus} onChange={(e) => setIfraStatus(e.target.value)}
                      data-testid="rm-modal-ifra"
                      className="w-full px-3 py-2 text-sm border rounded"
                      style={{ borderColor: "var(--ph-border-strong)" }}>
                {enums?.ifraStatus.values.map((s) => (
                  <option key={s} value={s}>{enums.ifraStatus.labels[s] || s}</option>
                ))}
              </select>
            </FormField>
            <FormField label="Cost per unit (EGP)">
              <input value={costPerUnit} onChange={(e) => setCostPerUnit(e.target.value)}
                     type="number" step="0.01"
                     className="w-full px-3 py-2 text-sm border rounded"
                     style={{ borderColor: "var(--ph-border-strong)" }} />
            </FormField>
            <FormField label="Unit">
              <select value={unit} onChange={(e) => setUnit(e.target.value)}
                      className="w-full px-3 py-2 text-sm border rounded"
                      style={{ borderColor: "var(--ph-border-strong)" }}>
                {enums?.unit.values.map((u) => (
                  <option key={u} value={u}>{u}</option>
                ))}
              </select>
            </FormField>
            <FormField label="Stock on hand">
              <input value={stockOnHand} onChange={(e) => setStockOnHand(e.target.value)}
                     type="number"
                     className="w-full px-3 py-2 text-sm border rounded"
                     style={{ borderColor: "var(--ph-border-strong)" }} />
            </FormField>
            <FormField label="Lead time (days)">
              <input value={leadTimeDays} onChange={(e) => setLeadTimeDays(e.target.value)}
                     type="number"
                     className="w-full px-3 py-2 text-sm border rounded"
                     style={{ borderColor: "var(--ph-border-strong)" }} />
            </FormField>
          </div>
          <FormField label="Safety notes">
            <textarea value={safetyNotes} onChange={(e) => setSafetyNotes(e.target.value)} rows={2}
                      className="w-full px-3 py-2 text-sm border rounded"
                      style={{ borderColor: "var(--ph-border-strong)" }} />
          </FormField>
        </div>
        <div className="p-4 border-t flex justify-end gap-2" style={{ borderColor: "var(--ph-border-subtle)" }}>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={onSubmit} disabled={busy || !code || !name} testId="rm-modal-save">
            <Save size={14} /> {busy ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function FormField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold">{label}</span>
      {children}
    </label>
  );
}

// ── Used In cell (inline chip + click to open detail) ────────────
function UsageCell({ rm, onOpen }: { rm: RawMaterial; onOpen: () => void }) {
  const u = rm.usage || { formulaCount: 0, productCount: 0, products: [] };
  const isUsed = u.formulaCount > 0;
  return (
    <button
      onClick={onOpen}
      disabled={!isUsed}
      className="text-left group"
      title={isUsed ? "Show products using this material" : "Not used in any formula yet"}
      data-testid={`rm-usage-${rm.code}`}
    >
      <div className="text-[11px] font-semibold" style={{ color: isUsed ? "var(--ph-brand-primary)" : "var(--ph-ink-muted)" }}>
        {u.formulaCount} formula{u.formulaCount === 1 ? "" : "s"}
      </div>
      <div className="text-[10px]" style={{ color: "var(--ph-ink-muted)" }}>
        {u.productCount} product{u.productCount === 1 ? "" : "s"}
        {isUsed && <span className="ml-1 opacity-70 group-hover:opacity-100">·  view</span>}
      </div>
    </button>
  );
}

// ── Used In popover (products using this raw material) ───────────
function UsagePopover({ rm, onClose }: { rm: RawMaterial; onClose: () => void }) {
  const products = rm.usage?.products || [];
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
         style={{ background: "rgba(0,0,0,0.4)" }} onClick={onClose}>
      <div className="w-full max-w-lg bg-white rounded shadow-2xl"
           onClick={(e) => e.stopPropagation()}
           data-testid="rm-usage-popover">
        <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: "var(--ph-border-subtle)" }}>
          <div>
            <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold">Used in products</div>
            <div className="text-lg font-bold">{rm.code} · {rm.name}</div>
            <div className="text-xs text-slate-500 mt-0.5">
              Referenced by {rm.usage?.formulaCount || 0} formula(s) across {products.length} product(s).
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:opacity-70"><X size={18} /></button>
        </div>
        <div className="max-h-[60vh] overflow-y-auto">
          {!products.length && (
            <div className="p-6 text-sm text-slate-500 text-center">
              This raw material is not referenced by any active formula yet.
            </div>
          )}
          {products.map((p) => (
            <div key={p.id}
                 className="flex items-center justify-between px-4 py-3 border-b hover:bg-slate-50"
                 style={{ borderColor: "var(--ph-border-subtle)" }}
                 data-testid={`rm-usage-product-${p.productCode}`}>
              <div className="flex items-center gap-3">
                <Package size={16} className="opacity-50" />
                <div>
                  <div className="text-sm font-semibold text-slate-900 font-mono">{p.productCode || "—"}</div>
                  <div className="text-xs text-slate-600">{p.nameEn || "—"}</div>
                </div>
              </div>
              <Link
                to={`/pureh/rd-lab?productId=${encodeURIComponent(p.id)}`}
                onClick={onClose}
                className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded transition"
                style={{ background: "var(--ph-brand-primary)", color: "#F7F3EB" }}
              >
                Open in R&D <ExternalLink size={11} />
              </Link>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

